<template>
  <div class="w-100">
    <AcknowledgementSettingsRevertModal
      :show="showRevertModal"
      :field-name="pendingFieldLabel"
      :default-value="pendingRevertDefault"
      @close="handleRevertModalClose"
      @confirm="handleRevertConfirm"
    />
    <AcknowledgementSettingsSkeleton v-if="pageLoading" />

    <div
      v-else
      class="form-wrapper"
    >
      <v-form class="acknowledgements-form">
        <div>
          <p class="text-h1 text-textDarkest mb-0 w-100">
            Organization Information
          </p>
          <p class="section-description mb-0">
            Customize this information to your organization.
            <a
              href="#"
              class="section-description-link"
              data-beacon-article-sidebar="69e26a240673286bddfb1b62"
              @click.stop
            >
              Learn more here.
            </a>
          </p>
        </div>

        <div class="two-col-grid">
          <div class="field-block">
            <p class="text-h2 text-textDarkest mb-0">
              Organization Name
            </p>
            <p class="section-desc">
              This defines how your organization will be referred to in acknowledgement request
              emails sent to employees.
              e.g. "{Organization} requests acknowledgement of your job profile."
            </p>
            <DpplFormField
              field-name="organization_name"
              :form="form"
              v-bind="$attrs"
              @update:model-value="handleFormValueUpdate($event)"
            >
              <template #field="{ props, on }">
                <DpplTextField
                  v-bind="props"
                  class="input-small"
                  :class="{ 'input-is-default': isFieldDefault('organization_name') }"
                  :placeholder="defaults.organization_name"
                  :disabled="componentLoading"
                  v-on="on"
                />
              </template>
            </DpplFormField>
            <RevertRow
              v-if="!isFieldDefault('organization_name')"
              :disabled="componentLoading"
              @revert="openRevertModal('organization_name')"
            />
          </div>
          <div class="field-block">
            <p class="text-h2 text-textDarkest mb-0">
              HR Contact Email
            </p>
            <p class="section-desc">
              The email address that an employee should contact if they have questions about their
              job profile acknowledgement.
            </p>
            <DpplFormField
              field-name="hr_contact_email"
              :form="form"
              v-bind="$attrs"
              @update:model-value="handleFormValueUpdate($event)"
            >
              <template #field="{ props, on }">
                <DpplTextField
                  v-bind="props"
                  class="input-small"
                  :class="{ 'input-is-default': isFieldDefault('hr_contact_email') }"
                  :placeholder="defaults.hr_contact_email"
                  :disabled="componentLoading"
                  v-on="on"
                />
              </template>
            </DpplFormField>
            <RevertRow
              v-if="!isFieldDefault('hr_contact_email')"
              :disabled="componentLoading"
              @revert="openRevertModal('hr_contact_email')"
            />
          </div>
        </div>

        <div class="w-100">
          <DpplNewButton
            color="primary"
            variant="flat"
            :disabled="!canSubmitOrgInfo"
            :loading="componentLoading"
            type="button"
            @click="handleSubmitOrgInfo"
          >
            Update Organization Information
          </DpplNewButton>
        </div>

        <div>
          <p class="text-h1 text-textDarkest mb-0 w-100">
            Acknowledgement Timelines
          </p>
          <p class="section-description mb-0">
            Update these timelines to receive alerts when employees are overdue for action.
            <a
              href="#"
              class="section-description-link"
              data-beacon-article-sidebar="69e26a240673286bddfb1b62"
              @click.stop
            >
              Learn more here.
            </a>
          </p>
        </div>

        <div class="two-col-grid">
          <div class="field-block">
            <p class="text-h2 text-textDarkest mb-0">
              Acknowledgement Cycle SLA
            </p>
            <p class="section-desc">
              How often employees are required to sign a new acknowledgement. You'll be alerted
              via the
              <router-link
                class="section-description-link"
                :to="{ name: 'employee-acknowledgements' }"
                target="_blank"
              >
                Employee Acknowledgements Report
              </router-link>
              when anyone is overdue. For an annual requirement, enter 365.
            </p>
            <div class="d-flex align-center ga-2">
              <DpplFormField
                field-name="acknowledgement_cycle_sla"
                :form="form"
                v-bind="$attrs"
                @update:model-value="handleFormValueUpdate($event)"
              >
                <template #field="{ props, on }">
                  <DpplTextField
                    v-bind="props"
                    class="input-numeric"
                    :class="{ 'input-is-default': isFieldDefault('acknowledgement_cycle_sla') }"
                    :placeholder="defaults.acknowledgement_cycle_sla"
                    type="text"
                    inputmode="numeric"
                    :disabled="componentLoading"
                    v-on="on"
                  />
                </template>
              </DpplFormField>
              <span class="days-suffix text-body-1 text-textDark">days</span>
            </div>
            <RevertRow
              v-if="!isFieldDefault('acknowledgement_cycle_sla')"
              :disabled="componentLoading"
              @revert="openRevertModal('acknowledgement_cycle_sla')"
            />
          </div>

          <div class="field-block">
            <p class="text-h2 text-textDarkest mb-0">
              Acknowledgement Reminder Threshold
            </p>
            <p class="section-desc">
              How many days an acknowledgement request can remain unsigned before you are alerted
              in the
              <router-link
                class="section-description-link"
                :to="{ name: 'employee-acknowledgements' }"
                target="_blank"
              >
                Employee Acknowledgements Report.
              </router-link>
              Enter 7 days to enable a weekly threshold.
            </p>
            <div class="d-flex align-center ga-2">
              <DpplFormField
                field-name="acknowledgement_pending_sla"
                :form="form"
                v-bind="$attrs"
                @update:model-value="handleFormValueUpdate($event)"
              >
                <template #field="{ props, on }">
                  <DpplTextField
                    v-bind="props"
                    class="input-numeric"
                    :class="{ 'input-is-default': isFieldDefault('acknowledgement_pending_sla') }"
                    :placeholder="defaults.acknowledgement_pending_sla"
                    type="text"
                    inputmode="numeric"
                    :disabled="componentLoading"
                    v-on="on"
                  />
                </template>
              </DpplFormField>
              <span class="days-suffix text-body-1 text-textDark">days</span>
            </div>
            <RevertRow
              v-if="!isFieldDefault('acknowledgement_pending_sla')"
              :disabled="componentLoading"
              @revert="openRevertModal('acknowledgement_pending_sla')"
            />
          </div>
        </div>

        <div class="w-100">
          <DpplNewButton
            color="primary"
            variant="flat"
            :disabled="!canSubmitTimelines"
            :loading="componentLoading"
            type="button"
            @click="handleSubmitTimelines"
          >
            Update Timelines
          </DpplNewButton>
        </div>

        <div class="d-flex flex-column ga-6 w-100">
          <div class="w-100 d-flex flex-column">
            <p class="text-h1 text-textDarkest mb-0 w-100">
              Acknowledgement Language
            </p>
            <p class="section-description mb-0">
              Update this to match your preferred language or any specific wording required by your
              Legal team.
              <a
                href="#"
                class="section-description-link"
                data-beacon-article-sidebar="69e26a240673286bddfb1b62"
                @click.stop
              >
                Learn more here.
              </a>
            </p>
          </div>
          <div class="field-block field-block-textarea">
            <p class="text-h2 text-textDarkest mb-0">
              Acknowledgement Email
            </p>
            <p class="section-desc">
              The introductory text included in the acknowledgement request email sent to employees.
            </p>
            <DpplFormField
              field-name="acknowledgement_email_intro"
              :form="form"
              v-bind="$attrs"
              @update:model-value="handleFormValueUpdate($event)"
            >
              <template #field="{ props, on }">
                <DpplTextArea
                  v-bind="props"
                  class="ack-textarea"
                  :class="{ 'input-is-default': isFieldDefault('acknowledgement_email_intro') }"
                  :placeholder="defaults.acknowledgement_email_intro"
                  auto-grow
                  :rows="TEXTAREA_MIN_ROWS"
                  :max-rows="TEXTAREA_MAX_ROWS"
                  :maxlength="MAX_CHAR_COUNT"
                  :disabled="componentLoading"
                  v-on="on"
                >
                  <template #append>
                    <span
                      class="text-body-3 char-count-append"
                      :class="{ 'char-count-append-error': charCountEmail >= MAX_CHAR_COUNT }"
                    >
                      {{ charCountEmail }}
                    </span>
                  </template>
                </DpplTextArea>
              </template>
            </DpplFormField>
            <div class="d-flex align-center ga-2">
              <RevertRow
                v-if="!isFieldDefault('acknowledgement_email_intro')"
                :disabled="componentLoading"
                @revert="openRevertModal('acknowledgement_email_intro')"
              />
            </div>
          </div>
          <div class="field-block field-block-textarea">
            <p class="text-h2 text-textDarkest mb-0">
              Acknowledgement Agreement
            </p>
            <p class="section-desc">
              The body of text in the acknowledgement form that employees will read and sign.
            </p>
            <DpplFormField
              field-name="acknowledgement_agreement_text"
              :form="form"
              v-bind="$attrs"
              @update:model-value="handleFormValueUpdate($event)"
            >
              <template #field="{ props, on }">
                <DpplTextArea
                  v-bind="props"
                  class="ack-textarea"
                  :class="{ 'input-is-default': isFieldDefault('acknowledgement_agreement_text') }"
                  :placeholder="defaults.acknowledgement_agreement_text"
                  auto-grow
                  :rows="TEXTAREA_MIN_ROWS"
                  :max-rows="TEXTAREA_MAX_ROWS"
                  :maxlength="MAX_CHAR_COUNT"
                  :disabled="componentLoading"
                  v-on="on"
                >
                  <template #append>
                    <span
                      class="text-body-3 char-count-append"
                      :class="{ 'char-count-append-error': charCountAgreement >= MAX_CHAR_COUNT }"
                    >
                      {{ charCountAgreement }}
                    </span>
                  </template>
                </DpplTextArea>
              </template>
            </DpplFormField>
            <div class="d-flex align-center ga-2">
              <RevertRow
                v-if="!isFieldDefault('acknowledgement_agreement_text')"
                :disabled="componentLoading"
                @revert="openRevertModal('acknowledgement_agreement_text')"
              />
            </div>
          </div>
          <div class="field-block field-block-textarea">
            <p class="text-h2 text-textDarkest mb-0">
              Acknowledgement PDF
            </p>
            <p class="section-desc">
              The text included alongside the employee's signature in the completed acknowledgement
              PDF.
            </p>
            <DpplFormField
              field-name="acknowledgement_pdf_text"
              :form="form"
              v-bind="$attrs"
              @update:model-value="handleFormValueUpdate($event)"
            >
              <template #field="{ props, on }">
                <DpplTextArea
                  v-bind="props"
                  class="ack-textarea"
                  :class="{ 'input-is-default': isFieldDefault('acknowledgement_pdf_text') }"
                  :placeholder="defaults.acknowledgement_pdf_text"
                  auto-grow
                  :rows="TEXTAREA_MIN_ROWS"
                  :max-rows="TEXTAREA_MAX_ROWS"
                  :maxlength="MAX_CHAR_COUNT"
                  :disabled="componentLoading"
                  v-on="on"
                >
                  <template #append>
                    <span
                      class="text-body-3 char-count-append"
                      :class="{ 'char-count-append-error': charCountPdf >= MAX_CHAR_COUNT }"
                    >
                      {{ charCountPdf }}
                    </span>
                  </template>
                </DpplTextArea>
              </template>
            </DpplFormField>
            <div class="d-flex align-center ga-2">
              <RevertRow
                v-if="!isFieldDefault('acknowledgement_pdf_text')"
                :disabled="componentLoading"
                @revert="openRevertModal('acknowledgement_pdf_text')"
              />
            </div>
          </div>
        </div>
        <div class="w-100">
          <DpplNewButton
            color="primary"
            variant="flat"
            :disabled="!canSubmitLanguage"
            :loading="componentLoading"
            type="button"
            @click="handleSubmitLanguage"
          >
            Update Acknowledgement Language
          </DpplNewButton>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { isEmpty, isEqual } from 'lodash-es';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplTextArea from '@/components/datapeople/DpplTextArea.vue';
import DpplFormField from '@/components/datapeople/DpplFormField.vue';
import AcknowledgementSettingsSkeleton from '@/components/settings/components/AcknowledgementSettingsSkeleton.vue';
import AcknowledgementSettingsRevertModal
  from '@/components/settings/components/AcknowledgementSettingsRevertModal.vue';
import RevertRow from '@/components/settings/components/AcknowledgementSettingsRevertRow.vue';
import { useAppLoading } from '@/stores/app/loading';
import { useAcknowledgementSettings } from '@/stores/settings/acknowledgementSettings';
import { useAcknowledgementSettingsForm } from '@/stores/settings/acknowledgementSettingsForm';
import { useTracking } from '@/stores/tracking/tracking';

const ORG_INFO_KEYS = ['organization_name', 'hr_contact_email'];
const TIMELINES_KEYS = ['acknowledgement_cycle_sla', 'acknowledgement_pending_sla'];
const LANGUAGE_KEYS = ['acknowledgement_email_intro', 'acknowledgement_agreement_text', 'acknowledgement_pdf_text'];

const MAX_CHAR_COUNT = 3000;
const TEXTAREA_MIN_ROWS = 7;
const TEXTAREA_MAX_ROWS = 14;

const FIELD_LABELS = {
  organization_name: 'Organization Name',
  hr_contact_email: 'HR Contact Email',
  acknowledgement_email_intro: 'Acknowledgement Email',
  acknowledgement_agreement_text: 'Acknowledgement Agreement',
  acknowledgement_pdf_text: 'Acknowledgement PDF',
  acknowledgement_cycle_sla: 'Acknowledgement Cycle SLA',
  acknowledgement_pending_sla: 'Acknowledgement Reminder Threshold',
};

const numericInputFieldKeys = new Set([
  'acknowledgement_cycle_sla',
  'acknowledgement_pending_sla',
]);

const appLoadingStore = useAppLoading();
const { componentLoading, pageLoading } = storeToRefs(appLoadingStore);

const acknowledgementSettingsForm = useAcknowledgementSettingsForm();
const {
  values,
  errors,
  errorMessages,
} = storeToRefs(acknowledgementSettingsForm);

const acknowledgementSettingsStore = useAcknowledgementSettings();
const { defaults } = storeToRefs(acknowledgementSettingsStore);
const { load, update } = acknowledgementSettingsStore;

const showRevertModal = ref(false);
const pendingRevertKey = ref(null);
const pendingFieldLabel = ref('');
const originalValuesOrgInfo = ref(null);
const originalValuesTimelines = ref(null);
const originalValuesLanguage = ref(null);

const pendingRevertDefault = computed(() => {
  const key = pendingRevertKey.value;

  if (!key) return '';

  return String(defaults.value?.[key] ?? '');
});

const isFieldDefault = (key) => {
  const current = String(values.value?.[key] ?? '');
  const defaultValue = String(defaults.value?.[key] ?? '');

  return current === defaultValue;
};

const openRevertModal = (key) => {
  pendingRevertKey.value = key;
  pendingFieldLabel.value = FIELD_LABELS[key] ?? key;
  showRevertModal.value = true;
};

const handleRevertModalClose = () => {
  showRevertModal.value = false;
};

const normalizeValues = (vals, keys) => keys.reduce((acc, key) => {
  const raw = vals?.[key];
  acc[key] = raw == null || raw === '' ? '' : String(raw);
  return acc;
}, {});

const captureOriginalValuesOrgInfo = () => {
  originalValuesOrgInfo.value = normalizeValues(values.value, ORG_INFO_KEYS);
};

const captureOriginalValuesTimelines = () => {
  originalValuesTimelines.value = normalizeValues(values.value, TIMELINES_KEYS);
};

const captureOriginalValuesLanguage = () => {
  originalValuesLanguage.value = normalizeValues(values.value, LANGUAGE_KEYS);
};

const captureOriginalValues = () => {
  captureOriginalValuesOrgInfo();
  captureOriginalValuesTimelines();
  captureOriginalValuesLanguage();
};

const getSectionForKey = (key) => {
  if (ORG_INFO_KEYS.includes(key)) return 'Organization Information';

  if (TIMELINES_KEYS.includes(key)) return 'Acknowledgement Timelines';

  if (LANGUAGE_KEYS.includes(key)) return 'Acknowledgement Language';

  return 'Acknowledgement Settings';
};

const isSectionValid = (keys) => keys.every((key) => !errorMessages.value?.[key]);

const isDirtyOrgInfo = computed(() => (
  !!originalValuesOrgInfo.value
  && !isEqual(normalizeValues(values.value, ORG_INFO_KEYS), originalValuesOrgInfo.value)
));

const isDirtyTimelines = computed(() => (
  !!originalValuesTimelines.value
  && !isEqual(normalizeValues(values.value, TIMELINES_KEYS), originalValuesTimelines.value)
));

const isDirtyLanguage = computed(() => (
  !!originalValuesLanguage.value
  && !isEqual(normalizeValues(values.value, LANGUAGE_KEYS), originalValuesLanguage.value)
));

const canSubmitOrgInfo = computed(() => (
  isSectionValid(ORG_INFO_KEYS) && isDirtyOrgInfo.value && !componentLoading.value
));

const canSubmitTimelines = computed(() => (
  isSectionValid(TIMELINES_KEYS) && isDirtyTimelines.value && !componentLoading.value
));

const canSubmitLanguage = computed(() => (
  isSectionValid(LANGUAGE_KEYS) && isDirtyLanguage.value && !componentLoading.value
));

const handleSubmitOrgInfo = async () => {
  const baseline = originalValuesOrgInfo.value;
  const current = normalizeValues(values.value, ORG_INFO_KEYS);

  const changedKeys = baseline
    ? ORG_INFO_KEYS.filter((key) => current[key] !== baseline[key])
    : [];

  const success = await update(ORG_INFO_KEYS);

  if (success) {
    changedKeys.forEach((key) => {
      useTracking().ackSettingsUpdated({
        section: 'Organization Information',
        field: FIELD_LABELS[key],
      });
    });

    captureOriginalValuesOrgInfo();
  }
};

const handleSubmitTimelines = async () => {
  const baseline = originalValuesTimelines.value;
  const current = normalizeValues(values.value, TIMELINES_KEYS);

  const changedKeys = baseline
    ? TIMELINES_KEYS.filter((key) => current[key] !== baseline[key])
    : [];

  const success = await update(TIMELINES_KEYS);

  if (success) {
    changedKeys.forEach((key) => {
      useTracking().ackSettingsUpdated({
        section: 'Acknowledgement Timelines',
        field: FIELD_LABELS[key],
      });
    });

    captureOriginalValuesTimelines();
  }
};

const handleSubmitLanguage = async () => {
  const baseline = originalValuesLanguage.value;
  const current = normalizeValues(values.value, LANGUAGE_KEYS);

  const changedKeys = baseline
    ? LANGUAGE_KEYS.filter((key) => current[key] !== baseline[key])
    : [];

  const success = await update(LANGUAGE_KEYS);

  if (success) {
    changedKeys.forEach((key) => {
      useTracking().ackSettingsUpdated({
        section: 'Acknowledgement Language',
        field: FIELD_LABELS[key],
      });
    });

    captureOriginalValuesLanguage();
  }
};

const form = computed(() => ({
  values: values.value,
  errors: errors.value,
  errorMessages: errorMessages.value,
  labels: {},
}));

const charCountEmail = computed(() => String(values.value.acknowledgement_email_intro ?? '').length);
const charCountAgreement = computed(() => String(values.value.acknowledgement_agreement_text ?? '').length);
const charCountPdf = computed(() => String(values.value.acknowledgement_pdf_text ?? '').length);

const handleFormValueUpdate = (payload) => {
  if (isEmpty(payload || {})) return;

  const newValue = { ...payload };

  numericInputFieldKeys.forEach((key) => {
    // Remove non-numeric characters from numeric input fields
    if (key in newValue) {
      const value = newValue[key];
      const str = value === null || value === undefined ? '' : String(value);

      newValue[key] = str.replace(/\D/g, '');
    }
  });

  acknowledgementSettingsForm.setFormValues(newValue);
};

const revertField = (key) => {
  const defaultValue = defaults.value?.[key] ?? '';

  handleFormValueUpdate({ [key]: defaultValue });
};

const handleRevertConfirm = () => {
  const key = pendingRevertKey.value;

  if (key) {
    revertField(key);

    useTracking().ackSettingsReverted({
      section: getSectionForKey(key),
      field: FIELD_LABELS[key],
    });
  }

  showRevertModal.value = false;
};

onMounted(async () => {
  await load();
  captureOriginalValues();
});
</script>

<style scoped lang="less">
.form-wrapper {
  border-radius: 10px;
  background: @white;
  padding: 40px;
}

.acknowledgements-form {
  display: flex;
  flex-direction: column;
  gap: 35px;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
}

.two-col-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;
}

.section-desc {
  font-size: 16px;
  font-weight: 500;
  line-height: 150%;
  color: @text-dark;
}

.section-description {
  max-width: 100%;
  font-size: 16px;
  font-weight: 500 !important;
  line-height: 1.45;
  color: @text-dark;
}

.section-description-link {
  color: @text-brand;
  font-weight: 500 !important;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    opacity: 0.8;
  }
}

.field-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
}

.field-block-textarea {
  gap: 16px;
  align-items: stretch;
  width: 100%;
  max-width: 632px;
}

:deep(.input-small) {
  width: 268px;
  max-width: 100%;
}

:deep(.input-numeric) {
  width: 75px;
}

.days-suffix {
  padding-top: 2px;
}

:deep(.input-is-default input), :deep(.input-is-default textarea) {
  color: @text-lighter !important;
}

:deep(.ack-textarea .v-field) {
  width: 100%;
  border-radius: 8px;
}

:deep(.ack-textarea .v-field__field) {
  position: relative;
}

:deep(.ack-textarea .v-field__append-inner) {
  position: absolute;
  bottom: 10px;
  right: 12px;
}

.char-count-append {
  color: @text-lighter;
}

.char-count-append-error {
  color: @basic-red !important;
}
</style>
